import { useState } from 'react';

const areas = [
  'Intelligence & Research',
  'Strategy & Advisory',
  'Communication & IEC',
  'Technology & Innovation',
  'Not sure yet — let’s talk',
];

type Status = 'idle' | 'sending' | 'success' | 'error';

export default function Contact() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [brief, setBrief] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === 'sending') return;

    const form = e.currentTarget;
    const fd = new FormData(form);

    // Honeypot — silently treat as success so bots get no signal.
    const honeypot = (fd.get('website_alt') as string) ?? '';
    if (honeypot.trim() !== '') {
      setStatus('success');
      form.reset();
      setBrief('');
      return;
    }

    setStatus('sending');
    setFormError('');
    try {
      const params = new URLSearchParams();
      fd.forEach((value, key) => {
        if (typeof value === 'string' && value.trim() !== '' && key !== 'website_alt') {
          params.append(key, value);
        }
      });

      const res = await fetch('https://readdy.ai/api/form/da99ec4tvq5lvgtnavcg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const serverMsg = (meta?.message as string) || (parsed?.message as string) || (meta?.detail as string) || '';
      const code = parsed?.code as string | undefined;

      const isSpam = /spam/i.test(responseText) || /spam/i.test(serverMsg);
      if (!res.ok || isSpam || code !== 'OK') {
        setStatus('error');
        setFormError(serverMsg || 'Something went wrong sending your message. Please try again.');
        return;
      }

      setStatus('success');
      form.reset();
      setBrief('');
    } catch {
      setStatus('error');
      setFormError('A network problem stopped the message. Please try again or email us directly.');
    }
  };

  return (
    <section id="contact" className="relative w-full bg-background-50 py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 bg-dots-soft opacity-40 mask-radial-fade pointer-events-none"></div>

      <div className="relative px-6 md:px-16 lg:px-28">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Left — copy */}
          <div data-reveal className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-[10px] tracking-[0.3em] uppercase text-foreground-500 font-mono">11 / Contact</span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-[3.4rem] leading-[1.08] text-foreground-950 tracking-tight font-semibold">
              Have a challenge worth solving?
            </h2>
            <p className="mt-5 text-[16px] md:text-lg text-foreground-700">Tell us what you’re trying to achieve.</p>
            <p className="mt-3 max-w-md text-[15px] leading-[1.8] text-foreground-600">
              Whether it’s understanding a constituency, shaping a public campaign or
              building the systems behind a decision — start with a conversation.
              A real person will write back.
            </p>

            <div data-reveal-stagger className="mt-10 flex flex-col gap-6">
              <div className="stagger-child flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 shrink-0">
                  <i className="ri-mail-line w-5 h-5 flex items-center justify-center"></i>
                </span>
                <div>
                  <p className="text-[11px] tracking-[0.25em] uppercase text-foreground-400 font-mono mb-1">Email</p>
                  <a href="mailto:hello@prabhaw.in" className="text-[15px] text-foreground-900 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap">
                    hello@prabhaw.in
                  </a>
                </div>
              </div>
              <div className="stagger-child flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 shrink-0">
                  <i className="ri-map-pin-2-line w-5 h-5 flex items-center justify-center"></i>
                </span>
                <div>
                  <p className="text-[11px] tracking-[0.25em] uppercase text-foreground-400 font-mono mb-1">Office</p>
                  <p className="text-[15px] text-foreground-900">New Delhi, India</p>
                </div>
              </div>
              <div className="stagger-child flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 shrink-0">
                  <i className="ri-chat-smile-2-line w-5 h-5 flex items-center justify-center"></i>
                </span>
                <div>
                  <p className="text-[11px] tracking-[0.25em] uppercase text-foreground-400 font-mono mb-1">Response time</p>
                  <p className="text-[15px] text-foreground-900">Within one business day</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div data-reveal="scale" style={{ ['--reveal-delay' as never]: '120ms' }} className="lg:col-span-3">
            <form
              id="prabhaw-contact"
              data-readdy-form
              onSubmit={handleSubmit}
              className="relative rounded-[24px] border border-background-200 bg-background-100/60 p-8 md:p-10"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                <label className="flex flex-col gap-2">
                  <span className="text-[11px] tracking-[0.2em] uppercase text-foreground-500 font-mono">Organisation</span>
                  <input
                    name="organisation"
                    type="text"
                    maxLength={80}
                    placeholder="Your organisation"
                    className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-[15px] text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-500 transition"
                  />
                </label>
                <label className="flex flex-col gap-2">
                  <span className="text-[11px] tracking-[0.2em] uppercase text-foreground-500 font-mono">Your name</span>
                  <input
                    name="name"
                    type="text"
                    required
                    maxLength={60}
                    placeholder="Full name"
                    className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-[15px] text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-500 transition"
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                <label className="flex flex-col gap-2">
                  <span className="text-[11px] tracking-[0.2em] uppercase text-foreground-500 font-mono">Work email</span>
                  <input
                    name="email"
                    type="email"
                    required
                    maxLength={120}
                    placeholder="you@organisation.in"
                    className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-[15px] text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-500 transition"
                  />
                </label>
                <label className="flex flex-col gap-2">
                  <span className="text-[11px] tracking-[0.2em] uppercase text-foreground-500 font-mono">Phone</span>
                  <input
                    name="phone"
                    type="tel"
                    maxLength={30}
                    placeholder="+91"
                    className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-[15px] text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-500 transition"
                  />
                </label>
              </div>

              <label className="flex flex-col gap-2 mb-5">
                <span className="text-[11px] tracking-[0.2em] uppercase text-foreground-500 font-mono">Area of interest</span>
                <select
                  name="areaOfInterest"
                  required
                  defaultValue={areas[0]}
                  className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-[15px] text-foreground-950 outline-none focus:border-primary-500 transition cursor-pointer"
                >
                  {areas.map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col gap-2 mb-2">
                <span className="text-[11px] tracking-[0.2em] uppercase text-foreground-500 font-mono">Brief about the requirement</span>
                <textarea
                  name="brief"
                  required
                  rows={5}
                  maxLength={500}
                  value={brief}
                  onChange={(e) => setBrief(e.target.value)}
                  placeholder="What are you trying to achieve? What does success look like?"
                  className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-[15px] text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-500 transition resize-none"
                />
                <span className="text-[11px] text-foreground-400 font-mono self-end">{brief.length}/500</span>
              </label>

              {/* Honeypot */}
              <div className="form-honeypot" aria-hidden="true">
                <label htmlFor="website_alt">Website</label>
                <input
                  id="website_alt"
                  name="website_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="group inline-flex items-center justify-center gap-3 px-8 py-4 rounded-md bg-primary-500 text-background-50 text-[15px] font-medium hover:bg-primary-600 transition cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <span>{status === 'sending' ? 'Sending…' : 'Start a conversation'}</span>
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center group-hover:translate-x-0.5 transition-transform"></i>
                </button>
                {status === 'success' && (
                  <span className="text-[13px] text-primary-700 font-mono flex items-center gap-2">
                    <i className="ri-check-line w-4 h-4 flex items-center justify-center"></i>
                    Message received — we’ll reply within one business day.
                  </span>
                )}
                {status === 'error' && (
                  <span className="text-[13px] text-red-600 font-mono flex items-center gap-2">
                    <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center"></i>
                    {formError}
                  </span>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}